/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import type { SearchRequest } from '@modules/cloud-api/v1/types'

import { getFilterQuery, transformIdQuery } from './DeploymentFilterContext'

import type { SortState } from './types'

type BuildParams = {
  esQuery: any
  deletedDeploymentIds?: string[]
  size?: number
  cursor?: string
  sort?: SortState
}

export default function searchDeploymentsQuery(
  { esQuery = null, deletedDeploymentIds = [], size = 30, cursor, sort }: BuildParams = {
    esQuery: null,
  },
): SearchRequest {
  const matchDeletedDeploymentIds = deletedDeploymentIds.map(transformIdQuery)
  const filterQuery = getFilterQuery(esQuery)

  // deployments without at least an ES resource are effectively deleted for UI purposes
  const haveAtLeastOneEsResource = {
    nested: {
      path: `resources.elasticsearch`,
      query: {
        exists: {
          field: `resources.elasticsearch.id`,
        },
      },
    },
  }

  const query = {
    bool: {
      must: [haveAtLeastOneEsResource, ...(filterQuery === null ? [] : [filterQuery])],
      must_not: matchDeletedDeploymentIds,
    },
  }

  const esSort = sort ? mapSort(sort) : false

  const request: SearchRequest = {
    query,
    ...(typeof size === 'number' ? { size } : {}),
    ...(cursor ? { cursor } : {}),
    ...(esSort ? { sort: esSort } : {}),
  }

  return request
}

function mapSort(sort: SortState) {
  const direction = sort.direction

  // Map UI fields to ES sortable fields
  const fieldMap: Record<string, string> = {
    name: 'name.keyword',
    version: 'resources.elasticsearch.info.plan_info.current.plan.elasticsearch.version',
  }

  const field = fieldMap[sort.field]

  if (!field) {
    return undefined
  }

  return [{ [field]: direction }]
}
